import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '@/lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useSettings } from '@/hooks/useSettings';

const FloatingCTA = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { settings } = useSettings();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    type: 'Personal',
    companyName: '',
    phone: '',
    email: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(timer);
  }, []);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'consultations'), {
        ...formData,
        status: 'Pendiente',
        createdAt: serverTimestamp()
      });
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setShowModal(false);
        setFormData({
          firstName: '',
          lastName: '',
          type: 'Personal',
          companyName: '',
          phone: '',
          email: '',
          message: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Error submitting consultation:', error);
      alert('Hubo un error al enviar tu consulta. Por favor, inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <>
      <div
        className={`fixed bottom-8 right-6 z-50 flex flex-col items-end gap-3 transition-all duration-500 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        {/* Expanded Options */}
        {expanded && (
          <div className="flex flex-col gap-2 items-end animate-fade-in">
            <a
              href={settings.academyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap flex items-center gap-2 bg-black/80 backdrop-blur-xl border border-[#8c52ff]/40 text-white text-sm font-medium px-4 py-2.5 rounded-full hover:bg-[#8c52ff]/20 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-graduation-cap-line text-[#8c52ff]" />
              {t('common.floating.academy')}
            </a>
            <button
              onClick={() => {
                setShowModal(true);
                setExpanded(false);
              }}
              className="whitespace-nowrap flex items-center gap-2 bg-black/80 backdrop-blur-xl border border-[#00c2ff]/40 text-white text-sm font-medium px-4 py-2.5 rounded-full hover:bg-[#00c2ff]/20 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-calendar-line text-[#00c2ff]" />
              {t('common.floating.consultation')}
            </button>
          </div>
        )}

        {/* Main Button */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-[#8c52ff] to-[#00c2ff] text-white shadow-lg shadow-purple-500/40 hover:scale-110 transition-all duration-300 cursor-pointer relative"
          aria-label="Quick actions"
        >
          <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#8c52ff] to-[#00c2ff] animate-ping opacity-30" />
          <i className={`text-xl transition-all duration-300 ${expanded ? 'ri-close-line' : 'ri-rocket-line'}`} />
        </button>
      </div>

      {/* Consultation Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-[#0a0010] border border-purple-500/30 rounded-2xl p-6 w-full max-w-lg relative animate-fade-in shadow-2xl overflow-y-auto max-h-[90vh]">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
            >
              <i className="ri-close-line text-2xl" />
            </button>
            
            <h3 className="text-2xl font-black text-white mb-2">Agendar Consulta</h3>
            <p className="text-white/60 text-sm mb-6">Completa el formulario para que nos pongamos en contacto contigo.</p>
            
            {success ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">¡Solicitud enviada!</h4>
                <p className="text-white/60 text-sm">Nos pondremos en contacto contigo muy pronto.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/70 text-xs font-semibold mb-1">Nombre *</label>
                    <input 
                      type="text" 
                      name="firstName" 
                      required 
                      value={formData.firstName} 
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#8c52ff]/60" 
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-xs font-semibold mb-1">Apellido *</label>
                    <input 
                      type="text" 
                      name="lastName" 
                      required 
                      value={formData.lastName} 
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#8c52ff]/60" 
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/70 text-xs font-semibold mb-1">Tipo de Consulta *</label>
                  <select 
                    name="type" 
                    value={formData.type} 
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#8c52ff]/60 appearance-none"
                  >
                    <option value="Personal" className="bg-[#0a0010]">Personal</option>
                    <option value="Empresarial" className="bg-[#0a0010]">Empresarial</option>
                  </select>
                </div>

                {formData.type === 'Empresarial' && (
                  <div className="animate-fade-in">
                    <label className="block text-white/70 text-xs font-semibold mb-1">Nombre de la Empresa *</label>
                    <input 
                      type="text" 
                      name="companyName" 
                      required={formData.type === 'Empresarial'} 
                      value={formData.companyName} 
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#8c52ff]/60" 
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/70 text-xs font-semibold mb-1">Teléfono *</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      required 
                      value={formData.phone} 
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#8c52ff]/60" 
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-xs font-semibold mb-1">Correo Electrónico *</label>
                    <input 
                      type="email" 
                      name="email" 
                      required 
                      value={formData.email} 
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#8c52ff]/60" 
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/70 text-xs font-semibold mb-1">Mensaje (Detalles) *</label>
                  <textarea 
                    name="message" 
                    required 
                    value={formData.message} 
                    onChange={handleChange}
                    rows={3}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#8c52ff]/60 resize-none" 
                  />
                </div>

                <button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white font-bold py-3 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 mt-2"
                >
                  {submitting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-fill" />}
                  {submitting ? 'Enviando...' : 'Enviar Solicitud'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingCTA;
